import { createAsyncThunk } from '@reduxjs/toolkit';
import { store } from '../../app/store';

import { showErrorToast } from '../gateway-toast/error-message-toast';

// import types.
import {
  CanfarServiceType,
  ComputeServiceType,
  JobType,
  Site,
  ToolType,
  UserType,
} from '../../utils/types';

// import functions
import { apiPrefix, fn_serviceName } from '../../utils/functions';

//  --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

// import state definition and functions (redux).
import {
  InteractiveSessionTab,
  InteractiveSessionsState,
  addTab,
  setSessionRefreshing,
  setSessionsLoading,
  addSessionsLoading,
  removeSessionsLoading,
  setSessions,
  addSessions,
  removeSessions,
  updateSessionComputeServiceId,
  updateSessionStatus,
  closeTab,
} from './interactive-sessions-slice';
import { DataState, rD_updateCanfarImages } from '../../app/data-slice';
import { GlobalState } from '../../app/global-slice';
import { getSelectedCanfar } from '../../app/canfar-selectors';
import {
  FileBrowserState,
  updateSkahaSessionID,
} from '../data-management/file-browser-slice';
import { setSelectedTool } from '../../app-slice';
import { autoStageForCatalog } from '../../app-actions';

//	--------------------------------------------------------------------------
//
//	F U N C T I O N S
//
//	--------------------------------------------------------------------------

export function findSessionForTab(
  tab?: InteractiveSessionTab,
): ComputeServiceType | undefined {
  const stateFetch: any = store.getState();
  const state: InteractiveSessionsState = stateFetch.interactiveSessions;

  if (tab?.compute_service_id !== undefined && tab?.compute_service_id !== '') {
    return state.sessions.find(
      (item) => item.compute_service_id === tab.compute_service_id,
    );
  }
  if (tab?.webSessionType !== undefined) {
    return state.sessions.find(
      (item) => item.resource_type === 'www' && item.session_type === tab.webSessionType,
    );
  }
  return undefined;
}

export function isClosableTab(tab: InteractiveSessionTab): boolean {
  return tab.compute_service_id !== undefined || tab.webSessionType !== undefined;
}

//	------------------------------------------------------------
//
//	Retrieves a list of active sessions from the selected
//	canfar instance.
//
//	------------------------------------------------------------

async function _getActiveSessionsCanfar(args: {
  canfarService: CanfarServiceType;
  thunkAPI: any;
  stateData: DataState;
}) {
  //
  let sessions: ComputeServiceType[] = [];

  if (args.canfarService.url !== '') {
    //
    var urlCommand: string =
      apiPrefix() +
      '/v1/skaha/session?url=' +
      args.canfarService.url +
      '&site_uuid=' +
      args.canfarService.site.site_uuid;

    try {
      const apiResult = await fetch(urlCommand, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      // Return code 200 means the API has run.
      if (apiResult.status === 200) {
        //
        // remove the current services.
        args.thunkAPI.dispatch(
          removeSessions({
            site_uuid: [args.canfarService.site.site_uuid ?? '#'],
            resource_type: ['canfar'],
            compute_status: ['running', 'pending', 'preparing', 'terminating', 'failed'],
          }),
        );

        const returnedJson = await apiResult.json();

        // find this CANFAR service in the local compute array.
        const serviceIndex: number = args.stateData.computeServices.findIndex(
          (element) => element.compute_service_id === args.canfarService.id,
        );
        let healthStatus: string | undefined = undefined;
        let activeStatus: boolean = false;
        if (serviceIndex > -1) {
          healthStatus = args.stateData.computeServices[serviceIndex].health_status;
          activeStatus =
            args.stateData.computeServices[serviceIndex].active_status === true;
        }

        // is this the currently-selected site?
        for (let i: number = 0; i < returnedJson.length; i++) {
          //
          const returnedSession: {
            id: string;
            image: string;
            type: string;
            status: string;
            name: string;
            start_time: string;
            expiry_time: string;
            connect_url: string;
            ram_in_use: string;
            cpu_cores_in_use: string;
            gpu_utilization: string;
          } = returnedJson[i];

          // only include CARTA, notebook, desktop, or headless sessions.
          if (
            (returnedSession.type.toLowerCase() === 'carta' ||
              returnedSession.type.toLowerCase() === 'notebook' ||
              returnedSession.type.toLowerCase() === 'desktop' ||
              returnedSession.type.toLowerCase() === 'headless') &&
            returnedSession.status.toLowerCase() !== 'failed' &&
            returnedSession.status.toLowerCase() !== 'terminating'
          ) {
            let newService: ComputeServiceType = {
              id: -1,
              site: args.canfarService.site ?? {
                node: undefined,
                site: undefined,
                site_uuid: undefined,
              },
              resource_type: 'canfar',
              session_type: returnedSession.type.toLowerCase(),
              compute_service_id: returnedSession.id,
              service_name: fn_serviceName({
                serviceType: returnedSession.type.toLowerCase(),
              }),
              session_name: returnedSession.name,
              service_url: returnedSession.connect_url,
              notebook_path: '',
              started_at: returnedSession.start_time,
              expires_at: returnedSession.expiry_time,
              is_embedded: returnedSession.type.toLowerCase() !== 'notebook',
              compute_status: returnedSession.status.toLowerCase(),
              active_status: activeStatus,
              top_banner:
                '@ ' +
                (args.canfarService.name !== ''
                  ? args.canfarService.name
                  : 'unknown CANFAR instance'),
              health_status: healthStatus,
            };
            sessions.push(newService);
          }
          //
        }
        //
      } else {
        //
        // turn off the refresh flag on current services.
        args.thunkAPI.dispatch(
          setSessionRefreshing({
            site_uuid: [args.canfarService.site.site_uuid ?? '#'],
            compute_status: ['running', 'pending'],
            resource_type: ['canfar'],
            refreshing: false,
          }),
        );
        //
      }
      //
    } catch (e) {}
    //
  }

  args.thunkAPI.dispatch(addSessions(sessions));
  args.thunkAPI.dispatch(
    removeSessionsLoading([
      {
        resource_type: 'canfar',
        compute_status: 'running',
        id: args.canfarService.id,
      },
    ]),
  );
  //
} // _getActiveSessionsCanfar

//	------------------------------------------------------------
//
//	Retrieves a list of active sessions from the SRCNet-defined
//	services.
//
//	------------------------------------------------------------

async function _getActiveSessionsSRCNet(args: { thunkAPI: any; stateData: DataState }) {
  //
  let sessions: ComputeServiceType[] = [];

  var urlCommand: string = apiPrefix() + '/v1/active_compute_sessions/grouped';

  try {
    const apiResult = await fetch(urlCommand, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    // Return code 200 means the API has run.
    if (apiResult.status === 200) {
      //
      const returnedJson = await apiResult.json();
      const groupedSessions: any = returnedJson['grouped_sessions'];

      // get a list of all the sites with active sessions.
      const sites: string[] = Object.keys(groupedSessions);
      for (let site of sites) {
        const services: any = groupedSessions[site];
        for (let i: number = 0; i < services.length; i++) {
          //
          // find this service in the local compute array.
          const serviceIndex: number = args.stateData.computeServices.findIndex(
            (element) => element.compute_service_id === services[i].compute_service_id,
          );
          let activeStatus: boolean | undefined = undefined;
          let activeSessionCount: number | undefined = undefined;
          let topBanner: string | undefined = undefined;
          let healthStatus: string | undefined = undefined;
          let bottomBannerType: string | undefined = undefined;
          let bottomBanner: string | undefined = undefined;
          if (serviceIndex > -1) {
            activeStatus = args.stateData.computeServices[serviceIndex]['active_status'];
            activeSessionCount =
              args.stateData.computeServices[serviceIndex]['active_session_count'];
            topBanner = args.stateData.computeServices[serviceIndex]['top_banner'];
            healthStatus = args.stateData.computeServices[serviceIndex]['health_status'];
            bottomBannerType =
              args.stateData.computeServices[serviceIndex]['bottom_banner_type'];
            bottomBanner = args.stateData.computeServices[serviceIndex]['bottom_banner'];
            /*bottomBannerType = 'from_to';
            bottomBanner = '{2026-05-31T10:32:15Z}{2026-06-03T23:17:36Z}';*/
          }
          if (
            args.stateData.computeServices[serviceIndex].session_type === 'carta' ||
            args.stateData.computeServices[serviceIndex].session_type === 'jupyterhub'
          ) {
            let newService: ComputeServiceType = {
              id: services[i]['id'],
              site: args.stateData.sites.find((item) => item.site_uuid === site) ?? {
                node: undefined,
                site: undefined,
                site_uuid: undefined,
              },
              resource_type: 'srcnet',
              session_type: args.stateData.computeServices[serviceIndex].session_type,
              compute_service_id: services[i]['compute_service_id'],
              service_name: fn_serviceName({
                serviceType: args.stateData.computeServices[serviceIndex].session_type,
              }),
              session_name: services[i]['service_name'],
              service_url: services[i]['service_url'],
              notebook_path: services[i]['notebook_path'],
              started_at: services[i]['started_at'],
              expires_at: services[i]['expires_at'],
              is_embedded: services[i]['is_embedded'] === 1,
              compute_status: services[i]['compute_status'],
              active_status: activeStatus,
              active_session_count: activeSessionCount,
              top_banner: topBanner,
              health_status: healthStatus,
              bottom_banner_type: bottomBannerType,
              bottom_banner: bottomBanner,
            };
            sessions.push(newService);
          }
          //
        }
        //
      }
      //
    }
    //
  } catch (e) {}

  args.thunkAPI.dispatch(
    removeSessions({
      resource_type: ['srcnet'],
      compute_status: ['running', 'pending'],
    }),
  );
  args.thunkAPI.dispatch(addSessions(sessions));
  args.thunkAPI.dispatch(
    removeSessionsLoading([
      {
        resource_type: 'srcnet',
        compute_status: 'running',
      },
    ]),
  );
  //
} // _getActiveSessionsSRCNet

//	------------------------------------------------------------
//
//	Retrieves a list of active sessions from the selected
//	canfar instance.
//
//	------------------------------------------------------------

async function _getAvailableSessionsCanfar(args: {
  canfarService: CanfarServiceType;
  thunkAPI: any;
  stateData: DataState;
}) {
  //
  let sessions: ComputeServiceType[] = [];

  if (args.canfarService.url !== '') {
    //
    var urlCommand: string =
      apiPrefix() +
      '/v1/skaha/images?url=' +
      args.canfarService.url +
      '&site_uuid=' +
      args.canfarService.site.site_uuid;

    try {
      const apiResult = await fetch(urlCommand, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      // Return code 200 means the API has run.
      if (apiResult.status === 200) {
        //
        // remove the current services.
        args.thunkAPI.dispatch(
          removeSessions({
            site_uuid: [args.canfarService.site.site_uuid ?? '#'],
            resource_type: ['canfar'],
            compute_status: ['available'],
          }),
        );

        const returnedJson = await apiResult.json();

        // find this CANFAR service in the local compute array.
        const serviceIndex: number = args.stateData.computeServices.findIndex(
          (element) => element.compute_service_id === args.canfarService.id,
        );
        let activeStatus: boolean = false;
        let healthStatus: string | undefined = undefined;
        if (serviceIndex > -1) {
          activeStatus =
            args.stateData.computeServices[serviceIndex].active_status === true;
          healthStatus = args.stateData.computeServices[serviceIndex].health_status;
        }

        // build a unique list of types.
        let imageTypes: string[] = [];

        var returnedImages: {
          id: string;
          types: string[];
        }[] = [];

        // loop through the returned images.
        for (let i: number = 0; i < returnedJson.length; i++) {
          //
          const returnedImage: {
            id: string;
            types: string[];
            digest?: string;
          } = Object.assign({}, returnedJson[i]);
          delete returnedImage.digest;

          // add to the array.
          returnedImages.push(returnedImage);

          // loop through the types.
          for (let j: number = 0; j < returnedImage.types.length; j++) {
            if (imageTypes.indexOf(returnedImage.types[j].toLowerCase()) === -1) {
              imageTypes.push(returnedImage.types[j].toLowerCase());
            }
          }
          //
        }

        // update the canfar instance with this list of images.
        args.thunkAPI.dispatch(
          rD_updateCanfarImages({ id: args.canfarService.id, images: returnedImages }),
        );

        // add sessions if they have the correct image type.
        const validTypes: string[] = ['CARTA', 'notebook', 'desktop', 'headless'];
        for (let i: number = 0; i < validTypes.length; i++) {
          if (imageTypes.indexOf(validTypes[i].toLowerCase()) > -1) {
            let newService: ComputeServiceType = {
              id: -1,
              site: args.canfarService.site ?? {
                node: undefined,
                site: undefined,
                site_uuid: undefined,
              },
              resource_type: 'canfar',
              session_type: validTypes[i].toLowerCase(),
              compute_service_id: args.canfarService.id ?? '',
              service_name: fn_serviceName({ serviceType: validTypes[i] }),
              service_url: '',
              is_embedded: true,
              compute_status: 'available',
              active_status: activeStatus,
              top_banner:
                '@ ' +
                (args.canfarService.name !== ''
                  ? args.canfarService.name
                  : 'unknown CANFAR instance'),
              health_status: healthStatus,
            };
            sessions.push(newService);
          }
        }
        //
      } else {
        //
        // turn off the refresh flag on current services.
        args.thunkAPI.dispatch(
          setSessionRefreshing({
            site_uuid: [args.canfarService.site.site_uuid ?? '#'],
            resource_type: ['canfar'],
            compute_status: ['available'],
            refreshing: false,
          }),
        );
        //
      }
      //
    } catch (e) {}
    //
  }

  args.thunkAPI.dispatch(addSessions(sessions));
  args.thunkAPI.dispatch(
    removeSessionsLoading([
      {
        resource_type: 'canfar',
        compute_status: 'available',
        id: args.canfarService.id,
      },
    ]),
  );
  //
} // _getAvailableSessionsCanfar

//	------------------------------------------------------------
//
//	Retrieves a list of active sessions from the SRCNet-defined
//	services.
//
//	------------------------------------------------------------

async function _getAvailableSessionsSRCNet(args: {
  thunkAPI: any;
  stateData: DataState;
}) {
  //
  let sessions: ComputeServiceType[] = args.stateData.computeServices.filter(
    (filter) => filter.session_type === 'carta' || filter.session_type === 'jupyterhub',
  );

  args.thunkAPI.dispatch(
    removeSessions({
      resource_type: ['srcnet'],
      compute_status: ['available'],
    }),
  );
  args.thunkAPI.dispatch(addSessions(sessions));
  //
} // _getAvailableSessionsSRCNet

//	-------------------------------------------------
//
//	extract a list of suitable local computer services
//	for the given site.
//
//	-------------------------------------------------

export function getComputeServices(args: {
  site: Site;
  sessionType?: string; // notebook, visualise, or desktop
  showAvailable?: boolean; // default: FALSE
}): ComputeServiceType[] {
  //
  const stateFetch: any = store.getState();
  const state: InteractiveSessionsState = stateFetch.interactiveSessions;

  // loop through the compute services to find items for this site.
  return state.sessions
    .filter((filter) => {
      //
      // if this service status is 'available' then check if there is a running
      // service we can show instead.
      let foundActive: boolean = false;
      if (
        filter.compute_status.toLowerCase() === 'available' &&
        args.showAvailable === true
      ) {
        foundActive =
          state.sessions.findIndex(
            (find) =>
              find.compute_status.toLowerCase() === 'running' &&
              ((find.resource_type?.toLowerCase() === 'srcnet' &&
                find.compute_service_id === filter.compute_service_id) ||
                (find.resource_type?.toLowerCase() === 'canfar' &&
                  find.session_type === filter.session_type)) &&
              find.site.site_uuid === filter.site.site_uuid,
          ) > -1;
      }

      // check if we have the correct type of services.
      let foundCorrectType: boolean = true;
      if (args.sessionType !== undefined) {
        foundCorrectType =
          (args.sessionType.toLowerCase() === 'notebook' &&
            ['notebook', 'jupyterhub'].indexOf(filter.session_type.toLowerCase()) > -1) ||
          (args.sessionType.toLowerCase() === 'visualise' &&
            ['carta'].indexOf(filter.session_type.toLowerCase()) > -1) ||
          (args.sessionType.toLowerCase() === 'desktop' &&
            ['desktop'].indexOf(filter.session_type.toLowerCase()) > -1);
      }

      // only use active sites that match the site filter.
      return (
        filter.site.site_uuid === args.site?.site_uuid &&
        filter.resource_type?.toLowerCase() === 'canfar' &&
        (filter.compute_status.toLowerCase() === 'running' ||
          (filter.compute_status === 'available' &&
            args.showAvailable === true &&
            foundActive === false)) &&
        foundCorrectType === true
      );
      //
    })
    .sort((a, b) => {
      //
      // sort these results to put active sessions first, and then on session type.
      let swap: boolean = false;
      if (
        a.compute_status.toLowerCase() !== 'running' &&
        b.compute_status.toLowerCase() === 'running'
      ) {
        swap = true;
      } else if (
        a.compute_status.toLowerCase() === 'running' &&
        b.compute_status.toLowerCase() !== 'running'
      ) {
        swap = false;
      } else if (a.session_type > b.session_type) {
        swap = true;
      } else if (a.session_type < b.session_type) {
        swap = false;
      } else if (a.service_name > b.service_name) {
        swap = true;
      }

      return swap ? 1 : -1;
      //
    });
  //
} // getComputeServices

//	------------------------------------------------------------
//
//	Display an active session in a new tab on the page.
//
//	------------------------------------------------------------

export function displaySession(args: {
  session: ComputeServiceType;
  infoDisplayed?: boolean;
}) {
  //
  // open a running service, either in a new browser tab or a new tab on the page.
  if (
    ['jupyterhub', 'notebook', 'carta', 'desktop', 'headless'].includes(
      args.session.session_type,
    ) &&
    (args.session.compute_status === 'running' ||
      (['pending', 'preparing', 'error', 'failed'].includes(
        args.session.compute_status,
      ) &&
        args.infoDisplayed === true))
  ) {
    //
    store.dispatch(
      addTab({
        service: args.session,
        infoDisplayed: args.infoDisplayed,
      }),
    );
    store.dispatch(setSelectedTool(ToolType.INTERACTIVE_SESSIONS));
    //
  }
  //
} // displaySession

//  ------------------------------------------------------------
//
//  Launch a CANFAR session, or re-use an existing CANFAR
//  session, and stage data if required.
//
//  Sessions may be launched directly, bypassing the new-session
//  dialog (used for STANDARD users), where all values are populated
//  automatically and the session is launched in flexible mode
//  (cores and memory omitted).
//
//  Alternatively, sessions may be launched from the new-session
//  dialog, where all values are specified by the user.
//
//  ------------------------------------------------------------

export async function launchCanfarSession(args: {
  service?: ComputeServiceType;
  serviceType?: string;
  stagingCatalog?: string;
  reuseExistingSession?: boolean; // default TRUE
  sessionName?: string;
  image?: string;
  cores?: string;
  ram?: string;
  cmd?: string;
  args?: string;
  env?: string;
}) {
  //
  const stateFetch: any = store.getState();
  const stateSessions: InteractiveSessionsState = stateFetch.interactiveSessions;
  const stateGlobal: GlobalState = stateFetch.global;

  // retrieve global properties.
  const sG_darkMode: boolean = stateGlobal.darkMode;
  const sG_site: Site | undefined = stateGlobal.site;

  if (sG_site === undefined) {
    showErrorToast('No site is selected for this project', sG_darkMode);
    return;
  }

  if (args.service === undefined && args.serviceType === undefined) {
    showErrorToast('Unknown service type', sG_darkMode);
    return;
  }

  // define items needed for launching new sessions.
  const now: number = Date.now();
  const serviceType: string =
    args.serviceType !== undefined
      ? args.serviceType
      : (args.service?.session_type ?? '');
  let image: string | undefined = undefined;
  let canfarService: CanfarServiceType | undefined = undefined;

  // re-use a running or pending session of the same type and site
  // rather than launching a new one.
  let sessionID: string = '';
  let session: ComputeServiceType | undefined = undefined;
  if (args.reuseExistingSession !== false) {
    session = stateSessions.sessions.find(
      (session) =>
        session.resource_type === 'canfar' &&
        session.session_type === serviceType &&
        (session.site.site_uuid ?? '') === sG_site.site_uuid &&
        ['running', 'pending', 'preparing'].includes(session.compute_status) &&
        [undefined, 'starting'].includes(session.overridden_status),
    );
  }

  // launch a new session
  let newSession: boolean = false;
  let showInfo: boolean = true;
  if (session === undefined) {
    //
    newSession = true;

    // resolve the currently-selected CANFAR instance (holds the url and images).
    // getSelectedCanfar resolves the id against state.data.canfarServices so the
    // instance always carries an up-to-date site (MAN-791 depends on SP-6708).
    // Tell the user rather than dead-ending silently when no instance resolves.
    canfarService = getSelectedCanfar(stateFetch);
    if (canfarService === undefined) {
      showErrorToast('Cannot find a CANFAR service running at this site', sG_darkMode);
      return;
    }

    // pick the first image for this service type. We show a separate error when
    // the resolved instance has no matching image, so the user can tell the two
    // failure modes apart.
    if (args.image === undefined) {
      image = canfarService.images?.find(
        (img) => img.types.indexOf(serviceType) > -1,
      )?.id;
    } else {
      image = args.image;
    }
    if (image === undefined) {
      showErrorToast('No images available to launch this session', sG_darkMode);
      return;
    }

    // generate the session name the same way the dialog does, and a temporary
    // string id that stands in for the session id until SKAHA returns the real
    // one (see updateSessionComputeServiceId).
    let name: string = '';
    const sessionSuffix: string = Math.floor(Math.random() * 1_000_000_000)
      .toString()
      .padStart(9, '0');
    const tempSessionId: string = 'tmp-' + now + '-' + sessionSuffix;
    if (args.sessionName === undefined) {
      name = serviceType + '-session-' + sessionSuffix;
    } else {
      name = args.sessionName;
    }

    session = {
      id: now,
      site: sG_site,
      resource_type: 'canfar',
      session_type: serviceType,
      compute_service_id: tempSessionId,
      service_name: name,
      service_url: '',
      is_embedded: true,
      compute_status: 'preparing',
      overridden_status: 'starting',
      top_banner: `@ ${canfarService.name || 'unknown CANFAR instance'}`,
      active_status: true,
    };

    store.dispatch(addSessions([session]));

    // get the session ID from the temporary one we have generated.
    sessionID = tempSessionId;
    //
  } else {
    //
    // get the session ID from the existing session, and then check if we need to display
    // the info page or go straight to the session
    sessionID = session.compute_service_id;
    showInfo = false;

    // check if this existing session is currently running.
    if (
      session.compute_status !== 'running' ||
      ['renewing', undefined].includes(session.overridden_status) === false
    ) {
      showInfo = true;
    }

    // check if there are any active staging jobs running for this session.
    const stateFileBrowser: FileBrowserState = stateFetch.fileBrowser;
    if (
      stateFileBrowser.dataManagementJobs.findIndex(
        (job: JobType) =>
          job.skaha_session_id === sessionID &&
          ['STAGING', 'RETRYING', 'UNCHECKED', 'PREPARING'].includes(
            job.status.toUpperCase(),
          ),
      ) > -1
    ) {
      showInfo = true;
    }

    // check if we are adding any staging jobs to this session.
    if (args.stagingCatalog) {
      showInfo = true;
    }
    //
  }

  // take the user to the interactive-session page and show the status tab.
  displaySession({
    session: session,
    infoDisplayed: showInfo,
  });

  // stage the catalog data immediately, against the temporary session id for new
  // sessions. The backend (MAN-757) reconciles this temporary id with the real
  // SKAHA session id once the session is created, so the staging job stays linked
  // after the updateSessionComputeServiceId swap below.
  if (args.stagingCatalog) {
    store.dispatch(
      autoStageForCatalog({
        catalog: args.stagingCatalog,
        toSiteUUID: session.site.site_uuid ?? '',
        skahaSessionId: sessionID,
      }),
    );
  }

  // tear down the placeholder session and its status tab, and tell the user,
  // when the launch fails after we have already opened the tab.
  const failLaunch = (): void => {
    /*store.dispatch(removeSessions({ id: [now], resource_type: ['canfar'] }));
      const tabs: InteractiveSessionTab[] = (store.getState() as any)
        .interactiveSessions.tabs;
      const tabIndex: number = tabs.findIndex(
        (tab) => tab.compute_service_id === sessionID,
      );
      if (tabIndex > -1) {
        store.dispatch(closeTab({ tab: tabIndex }));
      }*/

    // update the session status to 'error'.
    store.dispatch(
      updateSessionStatus({
        session_id: sessionID,
        status: 'error',
      }),
    );
    showErrorToast("We couldn't launch your session", sG_darkMode);
  };

  if (newSession === true) {
    //
    // launch the session. add cores and ram if these have been supplied.
    try {
      //
      let parameters: any = {
        url: canfarService?.url ?? '',
        site_uuid: session.site.site_uuid ?? '',
        name: session.service_name,
        image: image ?? '',
        temporary_session_id: sessionID,
      };
      if (serviceType !== 'headless') {
        parameters = { ...parameters, ...{ type: serviceType } };
      }
      if (args.cores !== undefined) {
        parameters = { ...parameters, ...{ cores: args.cores } };
      }
      if (args.ram !== undefined) {
        parameters = { ...parameters, ...{ ram: args.ram } };
      }
      if (args.cmd !== undefined) {
        parameters = { ...parameters, ...{ cmd: args.cmd } };
      }
      if (args.args !== undefined) {
        parameters = { ...parameters, ...{ args: args.args } };
      }
      if (args.env !== undefined) {
        parameters = { ...parameters, ...{ env: args.env } };
      }
      const queryParams = new URLSearchParams(parameters);

      const response = await fetch(
        `${apiPrefix()}/v1/skaha/session?${queryParams.toString()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        },
      );

      const result = await response.json();

      if (!response.ok) {
        failLaunch();
        return;
      }

      // replace the temporary session id with the real one (MAN-753).
      const skahaSessionId: string | undefined = result.session_id ?? undefined;
      if (skahaSessionId !== undefined) {
        //
        store.dispatch(
          updateSessionComputeServiceId({
            id: now,
            compute_service_id: skahaSessionId,
          }),
        );

        // reconcile the staging job(s) we created against the temporary id so
        // the DM-jobs list stays linked to the now-real session.
        store.dispatch(
          updateSkahaSessionID({
            old_session_id: sessionID,
            new_session_id: skahaSessionId,
          }),
        );
        //
      }

      store.dispatch(
        refreshCanfarServices({
          canfarService: canfarService,
          refreshAvailable: false,
        }),
      );
      //
    } catch (err) {
      failLaunch();
    }
    //
  }
  //
} // launchCanfarSession

//	------------------------------------------------------------
//
//	refresh the list of CANFAR services in the side panel.
//
//	------------------------------------------------------------

export const refreshCanfarServices = createAsyncThunk(
  //
  'interactiveSessions/refreshServices',
  //
  async (
    args: {
      canfarService?: CanfarServiceType;
      refreshActive?: boolean;
      refreshAvailable?: boolean;
    },
    thunkAPI,
  ) => {
    //
    // read the state variables.
    const stateFetch: any = thunkAPI.getState();
    const state: InteractiveSessionsState = stateFetch.interactiveSessions;
    const stateData: DataState = stateFetch.data;

    // build a list of CANFAR services to check.
    var checkCanfarServices: CanfarServiceType[] = stateData.canfarServices ?? [];
    if (args.canfarService !== undefined) {
      checkCanfarServices = [args.canfarService];
    }

    let taskFactories = [];
    for (let canfarService of checkCanfarServices) {
      //
      if (args.refreshActive !== false) {
        //
        thunkAPI.dispatch(
          addSessionsLoading([
            {
              resource_type: 'canfar',
              compute_status: 'running',
              id: canfarService.id,
            },
          ]),
        );

        // flag the current services as refreshing.
        thunkAPI.dispatch(
          setSessionRefreshing({
            site_uuid: [canfarService.site.site_uuid ?? '#'],
            compute_status: ['running', 'pending'],
            resource_type: ['canfar'],
            refreshing: true,
          }),
        );

        // get the active sessions from CANFAR.
        taskFactories.push(() =>
          _getActiveSessionsCanfar({
            canfarService: canfarService,
            thunkAPI: thunkAPI,
            stateData: stateData,
          }),
        );
        //
      }
      if (args.refreshAvailable !== false) {
        //
        thunkAPI.dispatch(
          addSessionsLoading([
            {
              resource_type: 'canfar',
              compute_status: 'available',
              id: canfarService.id,
            },
          ]),
        );

        // flag the current services as refreshing.
        thunkAPI.dispatch(
          setSessionRefreshing({
            site_uuid: [canfarService.site.site_uuid ?? '#'],
            resource_type: ['canfar'],
            compute_status: ['available'],
            refreshing: true,
          }),
        );

        // get the available sessions from CANFAR.
        taskFactories.push(() =>
          _getAvailableSessionsCanfar({
            canfarService: canfarService,
            thunkAPI: thunkAPI,
            stateData: stateData,
          }),
        );
        //
      }
      //
    }

    const CHUNK_SIZE = 3;
    for (let i = 0; i < taskFactories.length; i += CHUNK_SIZE) {
      const currentBatch = taskFactories.slice(i, i + CHUNK_SIZE);
      const runningTasks = currentBatch.map((fn) => fn());
      await Promise.all(runningTasks);
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    //
  },
  //
); // refreshCanfarServices

//	------------------------------------------------------------
//
//	refresh the list of SRCNet services in the side panel.
//
//	------------------------------------------------------------

export const refreshSRCNetServices = createAsyncThunk(
  //
  'interactiveSessions/refreshServices',
  //
  async (_, thunkAPI) => {
    //
    // read the state variables.
    const stateFetch: any = thunkAPI.getState();
    const state: InteractiveSessionsState = stateFetch.interactiveSessions;
    const stateData: DataState = stateFetch.data;
    const stateGlobal: GlobalState = stateFetch.global;

    // currently, we only retrieve SRCNet services in developer mode.
    if (stateGlobal.userType === UserType.DEVELOPER) {
      //
      thunkAPI.dispatch(
        addSessionsLoading([
          {
            resource_type: 'srcnet',
            compute_status: 'running',
          },
        ]),
      );

      // get the active sessions from SRCNet.
      _getActiveSessionsSRCNet({
        thunkAPI: thunkAPI,
        stateData: stateData,
      });

      if (stateData.computeServices.length > 0) {
        //
        // get the available sessions from SRCNet.
        _getAvailableSessionsSRCNet({
          thunkAPI: thunkAPI,
          stateData: stateData,
        });
        //
      } else {
        thunkAPI.dispatch(
          removeSessions({
            resource_type: ['srcnet'],
            compute_status: ['available'],
          }),
        );
      }
      //
    }
    //
  },
  //
); // refreshSRCNetServices
